<template>
  <CardSection :loading="isSavableStateLoading" :error="majorError">
    <template #header>
      Manage Tags for Category {{ selectedTagCategory ? `- ${selectedTagCategory.data.category_name}` : '' }}
    </template>

    <template #body>
      <SubSection sub-section-heading="Manage Tag" :sub-section-nav="subSectionNav">
        <template #body>
          <VeeForm ref="addTagForm" @submit="onAddTag">
            <div class="row">
              <TextInput v-model="selectedTag.name"
                name="name"
                label="Tag"
                rules="required"
                col-style="form-group-standard-column" />
            </div>

            <ActionToolbar :component-state="currentState" 
              :success-message="successMessage" 
              :primary-button-text="selectedTag.id ? `Update` : `Add`"
              secondary-button-text="Clear"
              toolbar-size="col-md-6"
              @secondary-click="clearItem"
              :error-message="formError" />  
          </VeeForm>

          <PortalTable ref="tag_index" table-id="tag_index"
            mode="local"
            :table-data="records"
            :total-records="records?.count"
            :loading="isSavableStateProcessing"
            empty="No tags found!"
            :sort-order="1"
            sort-field="name"
            :filter-values="filterValues"
            filter-display="row"
            @on-filter-change="onFilterChange">
            <template #headers>
              <div class="table-header">
                <span><b>Tag</b></span>
              </div>
            </template>
            <template #columns>
              <Column field="name" 
                header="Tag"
                filter-field="name"
                sortable
                :show-filter-menu="false">
                <template #filter="{ filterModel = { value: null }, filterCallback }">
                  <InputText v-model="filterModel.value"
                    type="text"
                    class="p-column-filter form-control"
                    placeholder="Search by name"
                    @input="filterCallback()" />
                </template>
              </Column>
              <Column field="created_by.full_name" header="Created By"/>
              <Column field="updated_by.full_name" header="Updated By" />
              <Column field="state" header="State"
                filter-field="state"
                :show-filter-menu="false"
                :filter-menu-style="{ width: '14rem' }"
                style="min-width: 14rem">
                <template #filter>
                  <MultiSelectFilter ref="state" 
                    filter-field="state"
                    option-value="name"
                    option-label="name"
                    class="form-control form-control-select"
                    :options="stateOptions"
                    @change="onChange" />
                </template>
                <template #body="slotProps">
                  {{ slotProps.data.disabled_at ? 'Disabled' : 'Enabled' }}
                </template>
              </Column>
              <Column header="Actions" class="btn-2-icon-group">
                <template #body="slotProps">
                  <div class="d-flex">
                    <a href="#" title="Edit" class="action btn btn-fill btn-outline-primary mx-2" @click.prevent="updateTag(slotProps.data)">
                      <font-awesome-icon class="desktop-action" :icon="['fas', 'fa-pen-to-square']" /><span class="mobile-action">Edit</span>
                    </a>        
                    <a href="#" :title="slotProps.data.disabled_at ? `Enable` : `Disable`" class="action btn btn-fill btn-outline-danger" @click.prevent="toggleTagState(slotProps.data)">
                      <span v-if="slotProps.data.disabled_at">Enable</span>
                      <span v-else>Disable</span>
                    </a>
                  </div>
                </template>
              </Column>
            </template>
          </PortalTable>
        </template>
      </SubSection>
    </template>
  </CardSection>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useTagSubSectionNav } from '@/src/components/admin-area/match-tags/useTagSubSectionNav';
// @ts-ignore
import CardSection from "@/src/components/shared/CardSection.vue";
// @ts-ignore
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
// @ts-ignore
import SubSection from "@/src/components/shared/SubSection.vue";
import { Form as VeeForm } from 'vee-validate';
// @ts-ignore
import TextInput from "@/src/components/shared/FormComponents/TextInput.vue";
// @ts-ignore
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import { FilterMatchMode } from '@primevue/core/api';
// @ts-ignore
import MultiSelectFilter from "@/src/components/shared/FormComponents/MultiSelectFilter.vue";
// @ts-ignore
import { useTagStore } from '@/src/stores/tag';
import { Tag } from "@/src/models/match/tag";
import { useSavableState } from '@/src/composables/useSavableState';

const route = useRoute();
const tagStore = useTagStore();

const {
  majorError,
  formError,
  currentState,
  isSavableStateLoading,
  isSavableStateProcessing,
  setSavableStateProcessing,
  setSavableStateIdle,
  setSavableStateSuccess,
  handleMajorError,
  handleFormError,
} = useSavableState();

const { subSectionNav } = useTagSubSectionNav();

// Template refs
const addTagForm = ref<any>(null);
const tag_index = ref<any>(null);
const state = ref<any>(null);

// Component state
const selectedTagCategory = ref<any>(null);
const selectedTag = ref<any>({});
const records = ref<any>([]);
const successMessage = ref<string | null>(null);

const stateOptions = [
  { code: 'enabled', name: "Enabled" },
  { code: 'disabled', name: "Disabled" }
];

const filterValues = ref({
  "name": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  "state": { value: null, matchMode: FilterMatchMode.IN }
});

const createNew = computed(() => selectedTag.value.id == null);

async function getRowData() {
  records.value = [];
  try {
    setSavableStateProcessing();
    selectedTagCategory.value = await tagStore.loadTagCategory(route.params.id);

    selectedTagCategory.value?.data?.tags.forEach((r: any) => {
      records.value.push(Tag.buildFromAPI(r));
    });

    setSavableStateIdle();
  } catch(error) {
    handleMajorError(error);
  }
}

async function onAddTag() {
  try {
    setSavableStateProcessing();
    const payload = {
      id: selectedTag.value.id,
      name: selectedTag.value.name,
      tag_category_id: route.params.id,
    };
    if (createNew.value) {
      await tagStore.createTag(payload);
      successMessage.value = 'Successfully created!';
    } else {
      await tagStore.updateTag(payload);
      successMessage.value = 'Successfully updated!';
    }
    await reload();
    setSavableStateSuccess();
  } catch(error) {
    handleFormError(error);
  }
}

async function updateTag(data: any) {
  selectedTag.value = {
    id: data.id,
    name: data.name,
    tag_category_id: route.params.id,
    disabled_at: data.disabled_at,
  };
  window.scrollTo({ top: 0, behavior: "smooth" });
}

async function clearItem() {
  selectedTag.value = { id: null, name: null, disabled_at: null };
  if (addTagForm.value) await addTagForm.value.resetForm();
}

async function toggleTagState(data: any) {
  try {
    setSavableStateProcessing();
    if (!data.disabled_at) {
      if (!confirm(`Are you sure you want to disable the tag (${data.name})?`)) {
        setSavableStateIdle();
        return;
      }
      selectedTag.value = await tagStore.disableTag(data.id, route.params.id);
    } else {
      if (!confirm(`Are you sure you want to enable the tag (${data.name})?`)) {
        setSavableStateIdle();
        return;
      }
      selectedTag.value = await tagStore.enableTag(data.id, route.params.id);
    }
    await reload();
    successMessage.value = 'Successfully updated!';
    setSavableStateSuccess();
  } catch(error) {
    handleMajorError(error);
  }
}

async function reload() {
  selectedTag.value = { name: null, details: null, tag_category_id: null, id: null, disabled_at: null };
  records.value = [];
  await getRowData();
  if (addTagForm.value) await addTagForm.value.resetForm();
}

function onChange(event: any) {
  tag_index.value?.resetFilters(event);
}

function onFilterChange(event: any) {
  if (event["state"] === "") state.value?.clearFilters();
}

onMounted(async () => {
  await getRowData();
});
</script>
